import { API_ROOT } from "config.js";

const USER_WIDGETS_LIST_ENDPOINT = `${API_ROOT}/api/dashboard/widgets/`;
const USER_VIEWS_LIST_ENDPOINT = `${API_ROOT}/api/dashboard/views/`;
const DEVICE_LIST_ENDPOINT = `${API_ROOT}/api/devices/`;
const DEVICE_DETAILS_ENDPOINT = `${API_ROOT}/api/device/`;
const DOCUMENTS_LIST_ENDPOINT = `${API_ROOT}/api/documents/`;

export async function getUserWidgets(axios) {
  try {
    const response = await axios.get(`${USER_WIDGETS_LIST_ENDPOINT}`);
    if (response.status < 200 || response.status >= 300) {
      throw new Error(response.statusText);
    }
    const data = response.data;
    return data;
  } catch (error) {
    if (error.response && [401, 404].includes(error.response.status)) {
      return { results: [] };
    }
    throw error;
  }
}

export async function getUserViews(axios, view_types) {
  const response = await axios.get(
    `${USER_VIEWS_LIST_ENDPOINT}?view_types=${view_types}`
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function getUserDevices(axios, options = {}) {
  let url = `${DEVICE_LIST_ENDPOINT}`;

  // Handle legacy deviceId parameter or new options object
  if (typeof options === "string") {
    // Legacy call: getUserDevices(axios, deviceId)
    url = `${DEVICE_DETAILS_ENDPOINT}staticdata/${options}`;
  } else if (options.deviceId !== undefined) {
    // New call with deviceId in options
    url = `${DEVICE_DETAILS_ENDPOINT}staticdata/${options.deviceId}`;
  } else {
    // Add pagination parameters to URL if provided
    const params = new URLSearchParams();
    if (options.page) {
      params.append("page", options.page);
    }
    if (options.page_size) {
      params.append("page_size", options.page_size);
    }
    if (options.search_term) {
      params.append("search_term", options.search_term);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  }

  try {
    const response = await axios.get(url);
    if (response.status < 200 || response.status >= 300) {
      throw new Error(response.statusText);
    }
    const data = response.data;
    return data;
  } catch (error) {
    if (error.response && [401, 404].includes(error.response.status)) {
      return { devices: [], total_count: 0 };
    }
    throw error;
  }
}

export async function createUserDevice(axios, deviceData) {
  let payload = deviceData;
  let url = `${DEVICE_LIST_ENDPOINT}`;
  if (deviceData.avatarFile) {
    payload = new FormData();
    payload.append("file", deviceData.avatarFile);
    delete deviceData["avatarFile"];

    const json = JSON.stringify(deviceData);
    const blob = new Blob([json], {
      type: "application/json"
    });
    payload.append("document", blob);
  }
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function removeUserDevice(axios, deviceId) {
  let url = `${DEVICE_DETAILS_ENDPOINT}staticdata/${deviceId}`;
  const response = await axios.delete(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function updateUserDevice(axios, deviceId, deviceData) {
  let payload = deviceData;
  let url = `${DEVICE_DETAILS_ENDPOINT}staticdata/${deviceId}`;
  if (deviceData.avatarFile) {
    payload = new FormData();
    payload.append("file", deviceData.avatarFile);
    delete deviceData["avatarFile"];

    const json = JSON.stringify(deviceData);
    const blob = new Blob([json], {
      type: "application/json"
    });
    payload.append("document", blob);
  }
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function getDeviceReports(axios, deviceId, reportType, options = {}) {
  const params = new URLSearchParams();
  if (options.day) {
    params.set("day", options.day);
  }
  if (options.weekStart) {
    params.set("week_start", options.weekStart);
  }
  if (options.month) {
    params.set("month", options.month);
  }
  let url = `${DEVICE_DETAILS_ENDPOINT}reportdata/${deviceId}/${reportType}`;
  const query = params.toString();
  if (query) {
    url = `${url}?${query}`;
  }
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function recalculateDeviceReports(
  axios,
  deviceId,
  periods = ["day", "week", "month"],
  options = {}
) {
  const url = `${DEVICE_DETAILS_ENDPOINT}recalculate-reports/${deviceId}`;
  const payload = { periods };
  if (options.day) {
    payload.day = options.day;
  }
  if (options.weekStart) {
    payload.week_start = options.weekStart;
  }
  if (options.month) {
    payload.month = options.month;
  }
  if (options.force) {
    payload.force = true;
  }
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function generateDeviceMonthlyReport(
  axios,
  deviceId,
  options = {}
) {
  const month = options.month || new Date().toISOString().slice(0, 7);
  const params = new URLSearchParams({ month });
  if (options.force) {
    params.set("force", "1");
  }

  const url = `${DEVICE_DETAILS_ENDPOINT}monthly-report/${deviceId}?${params.toString()}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function getMonthlyReportStatus(axios, deviceId, jobId) {
  const url = `${DEVICE_DETAILS_ENDPOINT}monthly-report-status/${deviceId}/${jobId}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function getStatusTypePreview(axios, deviceId, statusType) {
  const url = `${DEVICE_DETAILS_ENDPOINT}status-preview/${deviceId}`;
  const response = await axios.post(url, {
    status_type: statusType
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function getStatusTypeHelper(axios, deviceId = "") {
  const params = new URLSearchParams();
  if (deviceId) {
    params.append("device_id", deviceId);
  }
  const query = params.toString();
  const url = `${DEVICE_DETAILS_ENDPOINT}status-helper${query ? `?${query}` : ""}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function startReprocessDeviceStatuses(axios, deviceId, payload) {
  const url = `${DEVICE_DETAILS_ENDPOINT}reprocess-statuses/${deviceId}`;
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function getReprocessDeviceStatus(axios, deviceId, jobId) {
  const url = `${DEVICE_DETAILS_ENDPOINT}reprocess-statuses/${deviceId}/${jobId}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function getDeviceLogs(axios, deviceId, date, format) {
  let url = `${DEVICE_DETAILS_ENDPOINT}logs/${deviceId}?date=${date}&format=${format}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function getDeviceAlarms(axios, deviceId) {
  const url = `${DEVICE_DETAILS_ENDPOINT}alarms/${deviceId}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function createDeviceAlarm(axios, deviceId, payload) {
  const url = `${DEVICE_DETAILS_ENDPOINT}alarms/${deviceId}`;
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function deleteDeviceAlarm(axios, deviceId, alarmId) {
  const url = `${DEVICE_DETAILS_ENDPOINT}alarms/${deviceId}/${alarmId}`;
  const response = await axios.delete(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function getDeviceAlarmHistory(axios, deviceId, limit = 100) {
  const url = `${DEVICE_DETAILS_ENDPOINT}alarms/history/${deviceId}?limit=${limit}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return response.data;
}

export async function downloadDeviceData(axios, deviceId, deviceData) {
  let payload = deviceData;
  let url = `${DEVICE_DETAILS_ENDPOINT}dynamicdata/${deviceId}`;
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function markDeviceFavorite(axios, deviceId) {
  const response = await axios.get(
    `${DEVICE_LIST_ENDPOINT}markfavorite/${deviceId}`
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function unmarkDeviceFavorite(axios, deviceId) {
  const response = await axios.delete(
    `${DEVICE_LIST_ENDPOINT}removefavorite/${deviceId}`
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function getDeviceDocs(axios, deviceId) {
  let url = `${DOCUMENTS_LIST_ENDPOINT}by-device/${deviceId}`;
  const response = await axios.get(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function uploadDeviceDocs(axios, docData, file) {
  let url = `${DOCUMENTS_LIST_ENDPOINT}upload/`;
  let payload = new FormData();
  payload.append("file", file);
  const json = JSON.stringify(docData);
  const blob = new Blob([json], {
    type: "application/json"
  });
  payload.append("document", blob);
  const response = await axios.post(url, payload);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}

export async function deleteDocument(axios, document_id) {
  let url = `${DOCUMENTS_LIST_ENDPOINT}delete/${document_id}`;
  const response = await axios.delete(url);
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  const data = response.data;
  return data;
}
